/**
 * dashboardLayout — Admin-Dashboard: welche Bereiche sichtbar sind und in
 * welcher Reihenfolge. Reine Funktionen, keine Seiteneffekte; die Auswahl
 * wird je Benutzer gespeichert (siehe lib/userSettings.js).
 *
 * Layout-Form: { order: string[], hidden: string[] } — immer vollständig
 * (jede Widget-ID genau einmal in `order`), damit die UI nie Lücken sieht.
 */

/** Spaltenbreite auf Desktop (6er-Raster): full=6, wide=4, narrow=2, half=3.
 *  Standard-Reihenfolge geht ohne Lücken auf: 6 | 6 | 4+2 | 6 | 3+3 | 3+3 | 3+3. */
export const DASHBOARD_WIDGETS = Object.freeze([
  { id: 'kpis', label: 'Kennzahlen', description: 'Umsatz, Bestellungen, Ø Bestellwert, aktive Kunden und Neukunden im Zeitraum.', span: 'full', locked: true },
  { id: 'abgleich', label: 'Zahlungsabgleich', description: 'Anteil verbuchter Zahlungen (Buchhaltung).', span: 'full' },
  { id: 'umsatz', label: 'Umsatzverlauf', description: 'Umsatz oder Bestellungen pro Tag, Woche oder Monat.', span: 'wide' },
  { id: 'neukunden', label: 'Neue Kunden', description: 'Kontakte mit Erstbestellung im Zeitraum.', span: 'narrow' },
  { id: 'offene', label: 'Offene Bestellungen', description: 'Bestellungen im Status „Neu“ – mit Abschließen-Aktion.', span: 'full' },
  { id: 'status', label: 'Auftragsstatus', description: 'Verteilung der Bestellungen nach Status.', span: 'half' },
  { id: 'kontakte', label: 'Neueste Kontakte', description: 'Die fünf zuletzt angelegten Kontakte.', span: 'half' },
  { id: 'topkunden', label: 'Top Kunden', description: 'Rangliste nach Umsatz im Zeitraum.', span: 'half' },
  { id: 'topprodukte', label: 'Top Produkte', description: 'Rangliste der Produkte nach Umsatz.', span: 'half' },
  { id: 'aussendienst', label: 'Außendienst', description: 'Umsatz je Außendienst-Mitarbeiter und Besuchsberichte.', span: 'half' },
  { id: 'reaktivieren', label: 'Reaktivieren', description: 'Stammkunden ohne Bestellung seit 60+ Tagen.', span: 'half' },
]);

const WIDGET_IDS = DASHBOARD_WIDGETS.map((w) => w.id);
const BY_ID = Object.fromEntries(DASHBOARD_WIDGETS.map((w) => [w.id, w]));
const LOCKED_IDS = new Set(DASHBOARD_WIDGETS.filter((w) => w.locked).map((w) => w.id));

export const DEFAULT_DASHBOARD_LAYOUT = Object.freeze({
  order: Object.freeze([...WIDGET_IDS]),
  hidden: Object.freeze([]),
});

const SPAN_CLASS = {
  full: 'lg:col-span-6',
  wide: 'lg:col-span-4',
  half: 'lg:col-span-3',
  narrow: 'lg:col-span-2',
};

/** Tailwind-Klasse für die Spaltenbreite; unbekannt → volle Breite. */
export function widgetSpanClass(span) {
  return SPAN_CLASS[span] || SPAN_CLASS.full;
}

const uniqueKnown = (list) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list.filter((id) => typeof id === 'string' && BY_ID[id] && !seen.has(id) && seen.add(id));
};

/**
 * Macht aus beliebiger (auch kaputter) Eingabe ein vollständiges Layout.
 * @param {unknown} raw
 * @returns {{ order: string[], hidden: string[] }}
 */
export function normalizeDashboardLayout(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_DASHBOARD_LAYOUT;
  const order = uniqueKnown(raw.order);
  const missing = WIDGET_IDS.filter((id) => !order.includes(id));
  const hidden = uniqueKnown(raw.hidden).filter((id) => !LOCKED_IDS.has(id));
  const normalized = { order: [...order, ...missing], hidden };
  return isDefaultLayout(normalized) ? DEFAULT_DASHBOARD_LAYOUT : normalized;
}

/** Sichtbare Widget-Definitionen in Layout-Reihenfolge. */
export function visibleWidgets(layout) {
  const l = normalizeDashboardLayout(layout);
  const hidden = new Set(l.hidden);
  return l.order.filter((id) => !hidden.has(id)).map((id) => BY_ID[id]);
}

/** Alle Widgets in Layout-Reihenfolge mit Sichtbarkeits-Flag (für den Editor). */
export function layoutRows(layout) {
  const l = normalizeDashboardLayout(layout);
  const hidden = new Set(l.hidden);
  return l.order.map((id) => ({ ...BY_ID[id], visible: !hidden.has(id) }));
}

/** @returns {{ order: string[], hidden: string[] }} neues Layout */
export function setWidgetVisible(layout, id, visible) {
  const l = normalizeDashboardLayout(layout);
  if (!BY_ID[id] || LOCKED_IDS.has(id)) return l;
  const hidden = visible ? l.hidden.filter((h) => h !== id) : [...new Set([...l.hidden, id])];
  return { order: [...l.order], hidden };
}

/** Verschiebt ein Widget um `delta` Plätze (−1 = nach oben, +1 = nach unten). */
export function moveWidget(layout, id, delta) {
  const l = normalizeDashboardLayout(layout);
  const from = l.order.indexOf(id);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= l.order.length) return l;
  const order = [...l.order];
  order.splice(from, 1);
  order.splice(to, 0, id);
  return { order, hidden: [...l.hidden] };
}

export function isDefaultLayout(layout) {
  if (!layout) return true;
  return layout.hidden.length === 0 && layout.order.every((id, i) => id === WIDGET_IDS[i]);
}
